import { MetricCard } from "@components";
import { TrendDown01, TrendUp01 } from "@untitledui/icons";
import { cx } from "@/utils/cx";
import {
	REPORT_SUMMARY_CARD_ICONS,
	REPORT_SUMMARY_CATEGORY_COLORS,
	REPORT_SUMMARY_CATEGORY_NAMES,
	REPORT_SUMMARY_CATEGORY_TEXT_COLORS,
} from "../constants";
import type { ITypeCard } from "../types";
import { formatCurrency } from "../utils";

type ExpenseTrendIndicatorProps = {
	percentChange: number;
};

function ExpenseTrendIndicator({ percentChange }: ExpenseTrendIndicatorProps) {
	const isPositive = percentChange >= 0;

	return (
		<span
			className={cx(
				"inline-flex items-center gap-1 rounded-full px-2 py-1 font-medium text-xs",
				isPositive ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"
			)}
			data-slot="expense-trend"
		>
			{isPositive ? (
				<TrendUp01 aria-hidden className="size-3" />
			) : (
				<TrendDown01 aria-hidden className="size-3" />
			)}
			<span className="sr-only">
				{isPositive ? "Aumento de " : "Redução de "}
			</span>
			{Math.abs(percentChange).toFixed(1)}%
			<span className="sr-only"> em relação ao período de comparação</span>
		</span>
	);
}

type ExpenseReportSummaryCardProps = {
	item: ITypeCard;
	showTrend?: boolean;
	isLoading?: boolean;
};

function ExpenseReportSummaryCard({
	item,
	showTrend = true,
	isLoading = false,
}: ExpenseReportSummaryCardProps) {
	const label = REPORT_SUMMARY_CATEGORY_NAMES[item.type];
	const color = REPORT_SUMMARY_CATEGORY_TEXT_COLORS[item.type];
	const Icon = REPORT_SUMMARY_CARD_ICONS[item.type];
	const bgColor = REPORT_SUMMARY_CATEGORY_COLORS[item.type];

	return (
		<MetricCard
			isLoading={isLoading}
			headerAside={
				showTrend ? (
					<ExpenseTrendIndicator
						percentChange={item.percentChangeVsBaselinePeriod}
					/>
				) : undefined
			}
			icon={<Icon className={cx("size-5", color)} />}
			iconBackground={bgColor}
			title={label}
			value={formatCurrency(item.total)}
		/>
	);
}

type ExpensesReportsSummaryCardsProps = {
	items: ITypeCard[];
	isLoading?: boolean;
};

export function ExpensesReportsSummaryCards({
	items,
	isLoading = false,
}: ExpensesReportsSummaryCardsProps) {
	const cards: ITypeCard[] =
		items.length > 0
			? items
			: Object.keys(REPORT_SUMMARY_CATEGORY_NAMES).map((type) => ({
					type: Number(type) as ITypeCard["type"],
					total: 0,
					percentChangeVsBaselinePeriod: 0,
				}));

	return (
		<div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
			{cards.map((item) => (
				<ExpenseReportSummaryCard
					item={item}
					key={item.type}
					isLoading={isLoading && items.length === 0}
					showTrend={items.length > 0}
				/>
			))}
		</div>
	);
}
